import { AlbumImage } from '@/models/album-image';
import { cn } from '@/shadcn-utils';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    LoaderIcon,
} from 'lucide-react';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import React from 'react';

const GalleryDialogOverlay = React.forwardRef<HTMLDivElement, RadixDialog.DialogOverlayProps>(
    ({ className, ...props }, ref) => {
        return (
            <RadixDialog.Overlay
                {...props}
                ref={ref}
                className={cn('fixed inset-0 z-20 bg-black/80 backdrop-blur-xl', className)}
            />
        );
    },
);

GalleryDialogOverlay.displayName = 'GalleryDialogOverlay';

const GalleryDialogContent = React.forwardRef<HTMLDivElement, RadixDialog.DialogContentProps>(
    ({ className, ...props }, ref) => {
        return (
            <RadixDialog.Content
                {...props}
                ref={ref}
                className={cn(
                    'fixed left-1/2 top-1/2 z-30 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2',
                    'flex flex-col gap-6 px-6',
                    className,
                )}
            />
        );
    },
);

GalleryDialogContent.displayName = 'GalleryDialogContent';

type PaginationProps = PropsWithChildren<{
    currentIndex: number;
    itemsCount: number;
    onFirst: () => void;
    onPrev: () => void;
    onNext: () => void;
    onLast: () => void;
}>;

const Pagination = ({ currentIndex, itemsCount, ...props }: PaginationProps) => {
    const isFirst = useMemo(() => currentIndex === 0, [currentIndex]);
    const isLast = useMemo(() => currentIndex === itemsCount - 1, [currentIndex, itemsCount]);

    return (
        <div className="flex items-center justify-center gap-4 text-background">
            <div>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Pierwsze zdjęcie"
                    onClick={props.onFirst}
                    disabled={isFirst}
                >
                    <ChevronsLeftIcon size={16} />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Poprzednie zdjęcie"
                    onClick={props.onPrev}
                    disabled={isFirst}
                >
                    <ChevronLeftIcon size={16} />
                </Button>
            </div>
            {props.children}
            <div>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Następne zdjęcie"
                    onClick={props.onNext}
                    disabled={isLast}
                >
                    <ChevronRightIcon size={16} />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Ostatnie zdjęcie"
                    onClick={props.onLast}
                    disabled={isLast}
                >
                    <ChevronsRightIcon size={16} />
                </Button>
            </div>
        </div>
    );
};

type PreviewProps = {
    images: AlbumImage[];
};

const Preview = (props: PreviewProps) => {
    const sortedImages = useMemo(
        () => [...props.images].sort((a, b) => (a.index > b.index ? 1 : -1)),
        [props.images],
    );

    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [currentImage, setCurrentImage] = useState<AlbumImage>(sortedImages[0]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);

        const image = sortedImages[currentIndex];

        const img = new Image();
        img.src = image.src;
        img.onload = () => {
            setCurrentImage(image);
            setIsLoading(false);
        };
        img.onerror = () => {
            setIsLoading(false);
        };
        img.onabort = () => {
            setIsLoading(false);
        };

        return () => {
            setIsLoading(false);
        };
    }, [currentIndex, sortedImages]);

    const handlePrev = () => {
        if (isLoading) {
            return;
        }

        setCurrentIndex((index) => Math.max(index - 1, 0));
    };

    const handleGoToIndex = (index: number) => {
        if (isLoading) {
            return;
        }

        setCurrentIndex(index);
    };

    const handleNext = () => {
        if (isLoading) {
            return;
        }

        setCurrentIndex((index) => Math.min(index + 1, sortedImages.length - 1));
    };

    return (
        <>
            <div className="relative isolate aspect-album max-w-full select-none overflow-clip rounded-sm bg-black">
                {isLoading ? (
                    <div className="absolute -inset-10 z-10 flex items-center justify-center bg-black/80 text-white backdrop-blur-md">
                        <LoaderIcon size={16} strokeWidth={1.5} className="animate-spin" />
                    </div>
                ) : null}
                <img
                    src={currentImage.src}
                    alt={currentImage.name}
                    className="size-full object-contain"
                />
            </div>
            <Pagination
                currentIndex={currentIndex}
                itemsCount={sortedImages.length}
                onFirst={() => handleGoToIndex(0)}
                onPrev={handlePrev}
                onNext={handleNext}
                onLast={() => handleGoToIndex(sortedImages.length - 1)}
            >
                <div className="select-none text-xs font-medium">
                    {currentIndex + 1} z {sortedImages.length}
                </div>
            </Pagination>
        </>
    );
};

type GalleryDialogProps = React.PropsWithChildren<{
    title: string;
    images: AlbumImage[];
}>;

export const GalleryDialog = (props: GalleryDialogProps) => {
    return (
        <RadixDialog.Root>
            <RadixDialog.Trigger asChild>{props.children}</RadixDialog.Trigger>
            <RadixDialog.Portal>
                <GalleryDialogOverlay />
                <GalleryDialogContent aria-describedby={undefined}>
                    <RadixDialog.Title className="text-lg font-medium text-background">
                        {props.title}
                    </RadixDialog.Title>
                    <Preview images={props.images} />
                </GalleryDialogContent>
            </RadixDialog.Portal>
        </RadixDialog.Root>
    );
};
